import React from "react";
import { Box, Container, Heading, Text, VStack, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, SimpleGrid, Icon, HStack, Button } from "@chakra-ui/react";
import { FaWrench, FaDollarSign, FaTools, FaChartLine, FaPhone } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const SOLAR_GOLD_HOVER = "#a07e10";

const SolarFAQ: React.FC = () => {
  const faqCategories = [
    {
      category: "Installation",
      icon: FaWrench,
      questions: [
        {
          q: "How long does a typical solar installation take?",
          a: "Most residential solar installations are completed in 1-3 days. The timeline includes: Day 1 - Roof preparation and mounting system installation, Day 2 - Panel installation and electrical work, Day 3 - Inverter setup, testing, and grid connection. Commercial installations may take 1-2 weeks depending on system size."
        },
        {
          q: "Will solar panels damage my roof?",
          a: "No, when installed correctly by certified professionals, solar panels actually protect your roof from weather damage. We use industry-standard mounting systems that are designed to preserve roof integrity. Our installers are trained to properly seal all penetrations and maintain your roof warranty."
        },
        {
          q: "Do I need to upgrade my electrical panel?",
          a: "Not always. Most modern homes can accommodate solar without upgrades. However, if your panel is over 30 years old, near capacity, or doesn't meet current codes, an upgrade may be recommended. We assess this during our free site inspection and include any necessary upgrades in your quote."
        },
        {
          q: "What happens during a site inspection?",
          a: "Our site inspection typically takes 30-45 minutes and includes: roof measurements and condition assessment, electrical panel review, shading analysis, structural evaluation, and discussion of your energy goals. We'll provide recommendations and answer all your questions on the spot."
        }
      ]
    },
    {
      category: "Cost & Savings",
      icon: FaDollarSign,
      questions: [
        {
          q: "How much can I save with solar panels?",
          a: "Most Australian homeowners save $1,500-$3,000 per year on electricity bills. Savings depend on: your current electricity usage and rates, system size and quality, your location's solar exposure, and feed-in tariff rates. Our calculator can provide a personalized estimate based on your specific situation."
        },
        {
          q: "What government rebates and incentives are available?",
          a: "Australian solar owners benefit from: Small-scale Technology Certificates (STCs) which reduce upfront costs by $2,000-$5,000, feed-in tariffs for excess energy exported to the grid (rates vary by state and retailer), and various state-specific incentives. We handle all rebate applications as part of our service."
        },
        {
          q: "What is the payback period for solar panels?",
          a: "The typical payback period in Australia is 3-7 years, depending on system size, electricity usage, and location. After payback, you'll enjoy 15-20+ years of essentially free electricity. With rising energy costs, payback periods are getting shorter each year."
        },
        {
          q: "Can I get financing for my solar system?",
          a: "Yes, we offer flexible financing options including: interest-free payment plans for qualified buyers, green loans with competitive rates from major lenders, and the option to pay upfront for maximum savings. Our team can help you find the best financing solution for your budget."
        }
      ]
    },
    {
      category: "Maintenance",
      icon: FaTools,
      questions: [
        {
          q: "How much maintenance do solar panels require?",
          a: "Solar panels require minimal maintenance. Regular rainfall typically keeps them clean enough. We recommend: visual inspection every 6 months, professional cleaning every 1-2 years (or more in dusty areas), and annual system performance check. Most manufacturers recommend professional inspection every 3-5 years."
        },
        {
          q: "What warranty comes with solar panels?",
          a: "Our solar packages include comprehensive warranties: 25-year performance warranty on panels (guaranteed 80%+ output), 10-15 year product warranty on panels, 5-10 year warranty on inverters (with option to extend), and 10-year workmanship warranty on installation. We also offer extended warranty packages for complete peace of mind."
        },
        {
          q: "What if a panel stops working?",
          a: "If a panel fails during the warranty period, replacement is covered at no cost to you. Our monitoring system alerts us to any performance issues, often before you notice. We'll diagnose the problem remotely and schedule a technician visit if needed. Most issues are resolved within 48-72 hours."
        },
        {
          q: "Do solar panels work in cloudy weather?",
          a: "Yes, solar panels still generate power on cloudy days, just at reduced capacity (typically 10-25% of peak output). Australia's high annual sunshine hours mean your system will produce plenty of energy year-round. Our quotes are based on realistic local weather patterns, not perfect conditions."
        }
      ]
    },
    {
      category: "Performance",
      icon: FaChartLine,
      questions: [
        {
          q: "How much electricity will my solar system generate?",
          a: "A typical 6.6kW system in Australia produces 24-30kWh per day, or 8,800-10,900kWh annually. Output varies by: geographic location and seasonal changes, panel orientation and tilt angle, shading from trees or buildings, and panel quality and efficiency. We provide detailed production estimates for your specific location."
        },
        {
          q: "Can I go completely off-grid with solar?",
          a: "While technically possible, going completely off-grid requires significant battery storage and is rarely cost-effective for most homes. A better approach is a grid-connected system with battery backup, which provides: energy independence during outages, ability to store excess solar energy, lower upfront costs than full off-grid, and the security of grid connection when needed."
        },
        {
          q: "What happens to excess solar energy I generate?",
          a: "Excess energy is either: stored in your battery (if you have one) for use later, or exported to the grid in exchange for feed-in tariff credits on your bill. Feed-in tariffs typically range from 8-15 cents per kWh. Adding a battery lets you use more of your solar energy rather than exporting it at lower rates."
        },
        {
          q: "How do I monitor my solar system's performance?",
          a: "All our systems include real-time monitoring via smartphone app or web portal. You can track: current power generation and consumption, daily, monthly, and yearly energy production, system health and any performance issues, environmental impact (CO2 saved), and financial savings. We also monitor your system remotely to detect any problems early."
        }
      ]
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/faq"
        fallbackTitle="Solar FAQ - Frequently Asked Questions"
        fallbackDescription="Get answers to common questions about solar installation, costs, maintenance, and performance"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Help Center
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Frequently Asked Questions
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Everything you need to know about solar energy, installation, and savings
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Category Overview */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={12}>
          {faqCategories.map((category) => (
            <Box
              key={category.category}
              p={6}
              bg="white"
              borderRadius="xl"
              border="2px solid"
              borderColor={SOLAR_GOLD}
              textAlign="center"
              _hover={{ shadow: "lg", transform: "translateY(-4px)", borderColor: SOLAR_GOLD }}
              transition="all 0.3s"
            >
              <Icon as={category.icon} boxSize={10} color={SOLAR_GOLD} mb={3} />
              <Heading size="sm" mb={2}>{category.category}</Heading>
              <Text fontSize="sm" color="gray.600">
                {category.questions.length} questions
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* FAQ Accordions by Category */}
        {faqCategories.map((category) => (
          <Box key={category.category} mb={12}>
            <HStack mb={6} spacing={4}>
              <Icon as={category.icon} boxSize={8} color={SOLAR_GOLD} />
              <Heading size="lg">{category.category}</Heading>
            </HStack>

            <Accordion allowMultiple>
              {category.questions.map((faq, index) => (
                <AccordionItem
                  key={index}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  mb={3}
                  _last={{ mb: 0 }}
                >
                  <h2>
                    <AccordionButton
                      py={6}
                      px={6}
                      _hover={{ bg: "rgba(198, 146, 20, 0.08)" }}
                      _expanded={{ bg: "rgba(198, 146, 20, 0.08)", borderBottomRadius: 0 }}
                      borderRadius="lg"
                    >
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                          {faq.q}
                        </Text>
                      </Box>
                      <AccordionIcon color={SOLAR_GOLD} boxSize={6} />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={6} px={6} pt={4} bg="gray.50">
                    <Text color="gray.700" lineHeight="tall">
                      {faq.a}
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        ))}
      </Container>

      {/* Still Have Questions CTA */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.lg">
          <Box
            bg="white"
            p={12}
            borderRadius="2xl"
            shadow="lg"
            textAlign="center"
            border="2px solid"
            borderColor={SOLAR_GOLD}
          >
            <VStack spacing={6}>
              <Icon as={FaPhone} boxSize={16} color={SOLAR_GOLD} />
              <Heading size="xl">Still Have Questions?</Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Our solar experts are here to help. Get in touch for tailored advice, detailed quotes, or to schedule your free site inspection.
              </Text>
              <HStack spacing={4}>
                <Button
                  bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }}
                  size="lg"
                  leftIcon={<FaPhone />}
                  onClick={() => window.location.href = 'tel:1300663765'}
                >
                  Call 1300 663 765
                </Button>
                <Button
                  variant="outline"
                  bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }}
                  size="lg"
                  onClick={() => window.location.href = '/solar/contact'}
                >
                  Send a Message
                </Button>
              </HStack>

              <Box pt={6}>
                <Text fontSize="sm" color="gray.500" mb={4}>Or explore more:</Text>
                <HStack spacing={3} flexWrap="wrap" justify="center">
                  <Button
                    size="sm"
                    variant="ghost"
                    bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }}
                    onClick={() => window.location.href = '/solar/calculator'}
                  >
                    Use Calculator
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }}
                    onClick={() => window.location.href = '/solar/quote'}
                  >
                    Get Quote
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    bg={NAVY} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }}
                    onClick={() => window.location.href = '/solar/resources'}
                  >
                    View Resources
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </Box>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarFAQ;
